import { ArrowDownToLine, History, Settings, UserRound } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router'

import { useJobs, useMe } from '@/api/hooks'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui'
import { cn } from '@/lib/cn'

export function TopBar() {
  const { data: user } = useMe()
  const jobs = useJobs('active')
  const navigate = useNavigate()
  const location = useLocation()
  function openShortcut(to: string) {
    if (`${location.pathname}${location.search}` === to) return
    void navigate(to, { state: { fromAvatar: true } })
  }
  const activeJobs = (jobs.data?.results ?? []).filter(
    (job) => job.status === 'queued' || job.status === 'running',
  ).length
  const name = user?.display_name.trim() || user?.email || 'Your account'
  const initials = name.split(/\s+/).slice(0, 2).map((part) => Array.from(part)[0]).join('').toUpperCase()

  return (
    <header className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between px-4">
      <span className="text-lg font-semibold tracking-tight text-text md:invisible">
        Tunes
      </span>
      <div className="flex items-center gap-2">
        <Link
          to="/downloads"
          aria-label={activeJobs ? `Downloads, ${activeJobs} active` : 'Downloads'}
          title="Downloads"
          className={cn(
            'relative flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-surface-hover',
            activeJobs ? 'text-accent' : 'text-text-muted',
          )}
        >
          <ArrowDownToLine className="h-5 w-5" aria-hidden="true" />
          {activeJobs > 0 ? (
            <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-on-accent" aria-hidden="true">
              {activeJobs > 99 ? '99+' : activeJobs}
            </span>
          ) : null}
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={`Account menu for ${name}`} className="flex h-11 w-11 items-center justify-center rounded-full border border-accent/20 bg-accent-soft text-sm font-semibold text-accent transition-colors hover:bg-accent/20">
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64">
            <DropdownMenuLabel className="normal-case tracking-normal">
              <span className="block truncate text-sm font-semibold text-text">{name}</span>
              {user?.display_name ? <span className="mt-1 block truncate font-normal">{user.email}</span> : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem icon={Settings} onSelect={() => void navigate('/settings')}>Settings</DropdownMenuItem>
            <DropdownMenuItem icon={UserRound} onSelect={() => openShortcut('/settings?section=account')}>Account</DropdownMenuItem>
            <DropdownMenuItem icon={History} onSelect={() => openShortcut('/settings/history')}>Listening history</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
